import { useEffect, useMemo, useState } from 'react';

import SearchIcon from '@mui/icons-material/Search';
import {
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { EButtonColor } from 'constants/enums';
import { EToasterStatus, showToast } from 'utils/showToast';

import DataTable from 'components/DataTable/DataTable';
import DialogModal from 'components/Dialog/Dialog';

import { CouponFormValues } from '../../../types';

import { handleExportCsv } from './utils';

interface CouponsAllowedPlayersTableProps {
  couponFormValues: CouponFormValues;
  setFieldValue: (field: string, value: any) => void;
}

export default function CouponsAllowedPlayersTable({
  couponFormValues,
  setFieldValue
}: CouponsAllowedPlayersTableProps) {
  const { allowedPlayers } = couponFormValues;
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm]);

  const [isImportCSVModalOpen, setIsImportCSVModalOpen] = useState(false);

  const rowsPerPage = 5;

  const handleDelete = (playerId: string) => {
    const currentPlayers = allowedPlayers || [];
    const updatedPlayers = currentPlayers.filter((id) => id !== playerId);
    setFieldValue('allowedPlayers', updatedPlayers);
  };

  const tableColumns = [
    {
      field: 'playerId',
      headerName: 'Player ID',
      flex: 1,
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: 'Actions',
      flex: 0,
      type: 'actions',
      width: 30,
      disableReorder: true,
      disableColumnMenu: true,
      filterable: false,
      hideable: false,
      getActions: (params: any) => {
        return [
          <GridActionsCellItem
            label="Delete"
            onClick={() => handleDelete(params.row.playerId)}
            showInMenu
          />
        ];
      }
    }
  ];

  const filteredPlayers = useMemo(() => {
    const allPlayers = allowedPlayers || [];
    if (!searchTerm) {
      return allPlayers;
    }
    return allPlayers.filter((playerId) =>
      playerId.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allowedPlayers, searchTerm]);

  const totalPlayers = filteredPlayers.length;

  const getTableRows = useMemo(() => {
    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredPlayers
      .slice(startIndex, endIndex)
      .map((playerId: string) => ({
        _id: playerId,
        playerId
      }));
  }, [filteredPlayers, currentPage, rowsPerPage]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleImportFile = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv, text/csv';
    fileInput.style.display = 'none';

    fileInput.onchange = (event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        console.log('Selected file:', file);

        const reader = new FileReader();

        reader.onload = (e) => {
          try {
            const text = e.target?.result as string;
            if (typeof text !== 'string') {
              throw new Error('Failed to read file content as text.');
            }

            const playerIds = text
              .split('\n')
              .map((line) => line.trim())
              .filter((line) => line.length > 0)
              .slice(1); // Skip the header row

            if (playerIds.length === 0) {
              showToast({
                message:
                  'CSV file is empty or contains no valid player IDs after skipping the header.',
                status: EToasterStatus.ERROR
              });
              return;
            }

            if (playerIds.length > 10000) {
              showToast({
                message: 'Cannot import more than 10,000 player IDs.',
                status: EToasterStatus.ERROR
              });
              return;
            }

            setFieldValue('allowedPlayers', playerIds);

            showToast({
              message: `Imported ${playerIds.length} player IDs.`,
              status: EToasterStatus.SUCCESS
            });
            setIsImportCSVModalOpen(false);
          } catch (error: any) {
            console.error('Error processing CSV:', error);
            showToast({
              message: `Error processing CSV: ${error.message || 'Unknown error'}`,
              status: EToasterStatus.ERROR
            });
          }
        };

        reader.onerror = () => {
          console.error('Error reading file:', reader.error);
          showToast({
            message: 'Error reading file.',
            status: EToasterStatus.ERROR
          });
        };

        reader.readAsText(file);
      }
      document.body.removeChild(fileInput);
    };

    document.body.appendChild(fileInput);
    fileInput.click();
  };

  return (
    <>
      <Stack spacing={2} marginTop={3}>
        <Typography variant="h6">Allowed Players</Typography>
        <Typography variant="body2" color="textSecondary">
          Import a file with player IDs to limit this coupon to specific players
        </Typography>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          flexWrap="wrap"
        >
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: '200px', flexGrow: { xs: 1, sm: 0 } }}
          />
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => setIsImportCSVModalOpen(true)}
            >
              Import CSV
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleExportCsv(allowedPlayers)}
            >
              Export CSV
            </Button>
          </Stack>
        </Stack>

        <Stack
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            overflow: 'hidden',
            margin: 0
          }}
        >
          <DataTable
            columns={tableColumns}
            rows={getTableRows}
            loading={false}
            pagination={true}
            hideFooter={false}
            totalCount={totalPlayers}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            rowsPerPageOptions={[rowsPerPage]}
            pageSize={rowsPerPage}
            onNoData={
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ textAlign: 'center', width: '100%', py: 4 }}
              >
                {allowedPlayers?.length > 0 ? (
                  'No players match your search'
                ) : (
                  <>
                    All players can currently redeem this coupon. To limit
                    access, add players to the allow list.
                    <br />
                    Click Export CSV to create a template file.
                  </>
                )}
              </Typography>
            }
          />
        </Stack>
      </Stack>
      <DialogModal
        isOpen={isImportCSVModalOpen}
        headline={
          allowedPlayers?.length > 0
            ? 'Replace existing allowed players'
            : 'Add allowed players'
        }
        text={
          allowedPlayers?.length > 0
            ? 'Uploading this file will overwrite your current allowed players list'
            : 'Uploading this file will add the players to your allowed players list'
        }
        closeDialog={() => setIsImportCSVModalOpen(false)}
        buttons={[
          {
            text: 'Cancel',
            color: EButtonColor.SECONDARY,
            variant: 'outlined',
            func: () => {
              setIsImportCSVModalOpen(false);
            }
          },
          {
            text: 'Upload',
            color: EButtonColor.PRIMARY,
            variant: 'contained',
            func: () => {
              handleImportFile();
            }
          }
        ]}
      />
    </>
  );
}
